/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { PaginationState } from '@tanstack/react-table';
import { SingleValue } from 'react-select';
import ReportCard from 'src/components/ui/ReportCard/ReportCard';
import { Container } from 'src/components/layouts/Container/Container';
import { BadgeType } from 'src/components/ui/Badge/Badge';
import FilterRow from 'src/components/layouts/FilterRow/FilterRow';
import TabBar from 'src/components/layouts/TabBar/TabBar';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import Select, { Option } from 'src/components/ui/Select/Select';
// import ChooseAboModal from 'src/components/modals/ChooseAboModal/ChooseAboModal';
import classNames from 'classnames';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';

import { getPaginationOptions } from 'src/utils/general';
import useFilters from 'src/services/filters';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import { ReportType, useReportsQuery } from '../services/reports';
import styles from './styles/AdvancedReports.module.scss';
import NoResultsFound from '../components/ui/NoResultsFound/NoResultsFound';

enum TabKeys {
  ALL = 'all',
  FAVORITES = 'favorites',
}

const tabLabels: Record<TabKeys, string> = {
  [TabKeys.ALL]: 'reports.all',
  [TabKeys.FAVORITES]: 'reports.favorites',
};

function AdvancedReports() {
  const { t } = useTranslation();
  const DEFAULT_PAGE = 1;
  const DEFAULT_LIMIT = 10;

  const [unlocked, setUnlocked] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKeys) || TabKeys.ALL;
  const reportsFilterInstance = useFilters('df.report');
  const [selectedTab, setSelectedTab] = useState<TabKeys>(initialTab);
  const options = getPaginationOptions();
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: parseInt(options[0].value, 20),
  });

  const { data: reportCardsData } = useReportsQuery({
    domain: reportsFilterInstance.domain,
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
  });

  const currentPage = reportCardsData?.page || DEFAULT_PAGE;
  const itemsPerPage = reportCardsData?.size || DEFAULT_LIMIT;
  const totalPages = Math.ceil(Number(reportCardsData?.total) / itemsPerPage);

  const reportCards = reportCardsData?.items || [];

  const [filteredReportCards, setFilteredReportCards] = useState(reportCards);

  useEffect(() => {
    if (selectedTab === TabKeys.FAVORITES) {
      setFilteredReportCards(reportCards.filter((card: ReportType) => card.is_favorite));
    } else {
      setFilteredReportCards(reportCards);
    }
  }, [reportCards, selectedTab]);

  const handleTabClick = (tab: TabKeys) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  const tabs = [
    { label: t(tabLabels[TabKeys.ALL]), key: TabKeys.ALL, onClick: () => handleTabClick(TabKeys.ALL) },
    {
      label: t(tabLabels[TabKeys.FAVORITES]),
      key: TabKeys.FAVORITES,
      onClick: () => handleTabClick(TabKeys.FAVORITES),
    },
  ];

  useEffect(() => {
    const currentTab = searchParams.get('tab') as TabKeys;
    if (currentTab && currentTab !== selectedTab && Object.values(TabKeys).includes(currentTab)) {
      setSelectedTab(currentTab);
    }
  }, [searchParams]);

  // const handleModalClose = () => {
  //   // TODO: Implement unlock logic
  //   setUnlocked(true);
  // };

  const getIconByCategory = (category: string) => {
    switch (category) {
      case 'Product':
        return 'warehouse';
      case 'Outbound':
        return 'local-shipping';
      case 'Returns':
        return 'returns';
      default:
        return 'warehouse';
    }
  };

  const getBadgeTypeByCategory = (category: string) => {
    switch (category) {
      case 'Product':
        return BadgeType.Neutral5;
      case 'Outbound':
        return BadgeType.Neutral3;
      case 'Returns':
        return BadgeType.Neutral2;
      default:
        return BadgeType.Neutral3;
    }
  };

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      setPagination({ pageIndex: 0, pageSize: Number(selectedOption.value) });
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    setPagination({ ...pagination, pageIndex: newIndex });
  };

  const activeIndex = tabs.findIndex(tab => tab.key === selectedTab);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('advanced-reports');

  return (
    <Container className={classNames(styles.root)}>
      <HeadlineWithButtons headline={t('navbar.advanced_reports')} />
      <div className="flex flex-col justify-between h-[75vh]">
        <div className={classNames({ [styles.locked]: !unlocked })}>
          <div className="mb-24">
            <TabBar defaultIndex={activeIndex} tabs={tabs} />
          </div>
          <div className="mb-24">
            <FilterRow
              withSaveFilters={false}
              filterInstance={reportsFilterInstance}
              primaryKeys={[{ key: 'custom', operator: '=' }]}
              singleParam
            />
          </div>
          {!filteredReportCards.length && (
            <div className="flex w-full items-center justify-center h-full">
              <div className={styles.noResultsFallback}>
                <NoResultsFound title={t('reports.no_reports_found')} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-x-20 gap-y-20">
            {filteredReportCards.map((card: any) => {
              return (
                <Link to={`/advanced-reports/${card.code}`}>
                  <ReportCard
                    allowActions={accessRights?.read_write}
                    id={card.id}
                    key={card.id}
                    headline={card.name}
                    description={card.description}
                    icon={getIconByCategory(card.category)}
                    category={card.category}
                    badgeType={getBadgeTypeByCategory(card.category)}
                    isFavorite={card.is_favorite}
                  />
                </Link>
              );
            })}
          </div>
        </div>
        <div className="flex w-full justify-between items-center">
          <div className="flex gap-x-10 flex-1 items-center">
            <div>
              <Select
                onChange={handlePerPageUpdate}
                options={options}
                defaultValue={options.find(opt => opt.value === String(itemsPerPage)) || options[1]}
                menuPlacement="auto"
              />
            </div>
            <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
          </div>
          <div className="ml-auto">
            <div className="w-full my-20 flex items-center">
              <div className="ml-auto">
                <TablePagination
                  maxPage={totalPages}
                  onPaginationClick={handleTablePaginationClick}
                  currentPage={currentPage - 1}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* {!unlocked && ( */}
      {/*  <div className={styles.modal}> */}
      {/*    <ChooseAboModal isOpen onClose={handleModalClose} /> */}
      {/*  </div> */}
      {/* )} */}
    </Container>
  );
}

export default AdvancedReports;
